import { useId, type ReactNode } from 'react';
import type { AvatarDecorationId } from '@clanfire/shared';

/**
 * Decorations are drawn in a 125×125 box centred on the avatar, whose circle
 * is r=50 at (62.5, 62.5), so everything scales with the avatar size.
 */
const C = 62.5;

/** Positions `count` items evenly around the avatar at `radius`, starting at `start` degrees (0 = top). */
function around(count: number, radius: number, start = 0, span = 360) {
  return Array.from({ length: count }, (_, i) => {
    const angle = start + (span === 360 ? (360 / count) * i : (span / Math.max(1, count - 1)) * i);
    const rad = ((angle - 90) * Math.PI) / 180;
    return { x: C + radius * Math.cos(rad), y: C + radius * Math.sin(rad), angle, i };
  });
}

/** For SVG transforms that turn about (0, 0), not the part's own middle (see .hx-deco * in index.css). */
const SVG_ORIGIN = { transformBox: 'view-box', transformOrigin: '0 0' } as const;

const HEART = 'M0 3 C -5 -1, -3 -6, 0 -3 C 3 -6, 5 -1, 0 3 Z';
const SPARKLE = 'M0 -5 L1.2 -1.2 L5 0 L1.2 1.2 L0 5 L-1.2 1.2 L-5 0 L-1.2 -1.2 Z';

function art(id: AvatarDecorationId, uid: string): ReactNode {
  const g = (name: string) => `${uid}-${name}`;
  switch (id) {
    case 'flame':
      return (
        <>
          <defs>
            <linearGradient id={g('f')} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor="#ff4d1f" />
              <stop offset="0.6" stopColor="#ff9a3d" />
              <stop offset="1" stopColor="#ffe08a" />
            </linearGradient>
          </defs>
          <circle cx={C} cy={C} r={53} fill="none" stroke={`url(#${g('f')})`} strokeWidth={3.5} />
          {around(12, 55).map(({ x, y, angle, i }) => (
            <g key={i} transform={`translate(${x} ${y}) rotate(${angle})`}>
              <path
                className="hx-flame"
                style={{ animationDelay: `${(i % 4) * -0.22}s` }}
                d={i % 2 ? 'M0 0 C -3.5 -4, -2.5 -9, 0 -13 C 2.5 -9, 3.5 -4, 0 0 Z' : 'M0 0 C -2.5 -3, -1.8 -6, 0 -8.5 C 1.8 -6, 2.5 -3, 0 0 Z'}
                fill={`url(#${g('f')})`}
              />
            </g>
          ))}
        </>
      );
    case 'neon':
      return (
        <>
          <defs>
            <filter id={g('glow')} x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="2.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g className="hx-pulse" filter={`url(#${g('glow')})`}>
            <circle cx={C} cy={C} r={54} fill="none" stroke="#22d3ee" strokeWidth={3} />
            <circle cx={C} cy={C} r={58} fill="none" stroke="#e879f9" strokeWidth={1.6} strokeDasharray="18 7" />
          </g>
        </>
      );
    case 'frost':
      return (
        <>
          <circle cx={C} cy={C} r={53.5} fill="none" stroke="#bae6fd" strokeWidth={3} opacity={0.9} />
          <circle cx={C} cy={C} r={56.5} fill="none" stroke="#e0f2fe" strokeWidth={1} strokeDasharray="2 5" />
          {around(6, 56, 20).map(({ x, y, i }) => (
            <g key={i} transform={`translate(${x} ${y})`}>
              <g className="hx-twinkle" style={{ animationDelay: `${i * -0.37}s` }} stroke="#f0f9ff" strokeWidth={1.3} strokeLinecap="round">
                <line x1={0} y1={-5} x2={0} y2={5} />
                <line x1={-4.3} y1={-2.5} x2={4.3} y2={2.5} />
                <line x1={-4.3} y1={2.5} x2={4.3} y2={-2.5} />
              </g>
            </g>
          ))}
        </>
      );
    case 'sakura':
      return (
        <>
          <circle cx={C} cy={C} r={53.5} fill="none" stroke="#f9a8d4" strokeWidth={2} />
          {[...around(4, 55, -60, 80), ...around(3, 55, 120, 60)].map(({ x, y, angle }, i) => (
            <g key={i} transform={`translate(${x} ${y}) rotate(${angle + 30})`}>
              <g className="hx-sway" style={{ animationDelay: `${i * -0.45}s` }}>
                {[0, 72, 144, 216, 288].map((r) => (
                  <ellipse key={r} cx={0} cy={-3.2} rx={2.1} ry={3.4} transform={`rotate(${r})`} fill="#fbcfe8" stroke="#f472b6" strokeWidth={0.5} />
                ))}
                <circle r={1.2} fill="#fde047" />
              </g>
            </g>
          ))}
        </>
      );
    case 'leaves':
      return (
        <>
          <path d={`M ${C - 53} ${C} A 53 53 0 0 0 ${C + 53} ${C}`} fill="none" stroke="#65a30d" strokeWidth={1.6} />
          {around(15, 54, 95, 170).map(({ x, y, angle, i }) => (
            <g key={i} transform={`translate(${x} ${y}) rotate(${angle + (i % 2 ? 35 : -35)})`}>
              <ellipse rx={2.6} ry={6} fill={i % 3 === 0 ? '#4d7c0f' : i % 3 === 1 ? '#65a30d' : '#84cc16'} />
            </g>
          ))}
          {around(3, 55, 150, 60).map(({ x, y, i }) => (
            <circle key={`b${i}`} cx={x} cy={y} r={2.2} fill="#ef4444" />
          ))}
        </>
      );
    case 'crown':
      return (
        <>
          <defs>
            <linearGradient id={g('gold')} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#fff3b0" />
              <stop offset="1" stopColor="#e0a100" />
            </linearGradient>
          </defs>
          <g transform="translate(86 17) rotate(22)">
            <g className="hx-bob">
              <path d="M-16 8 L-13 -8 L-6 1 L0 -12 L6 1 L13 -8 L16 8 Z" fill={`url(#${g('gold')})`} stroke="#b7791f" strokeWidth={1} strokeLinejoin="round" />
              <rect x={-16} y={7} width={32} height={4} rx={1.5} fill="#d69e2e" />
              <circle cx={0} cy={-2} r={2} fill="#ef4444" />
              <circle cx={-9} cy={2} r={1.4} fill="#3b82f6" />
              <circle cx={9} cy={2} r={1.4} fill="#22c55e" />
            </g>
          </g>
        </>
      );
    case 'pumpkin':
      // Halloween: a jack-o'-lantern perched on the avatar, its face flickering like a candle inside.
      return (
        <>
          <defs>
            <radialGradient id={g('p')} cx="0.4" cy="0.35" r="0.75">
              <stop offset="0" stopColor="#ffb25c" />
              <stop offset="1" stopColor="#e0620d" />
            </radialGradient>
          </defs>
          <circle cx={C} cy={C} r={53} fill="none" stroke="#f97316" strokeWidth={2.5} strokeDasharray="9 5" opacity={0.85} />
          <g transform="translate(93 21) rotate(12) scale(1.3)">
            <g className="hx-bob">
              <path d="M-1.6 -10 L-1 -16.5 L2.2 -16 L1.6 -10 Z" fill="#4d7c0f" />
              <path d="M1.5 -15 C 5 -18, 8 -16, 9 -13" fill="none" stroke="#65a30d" strokeWidth={1.4} strokeLinecap="round" />
              <ellipse cx={-7} cy={0} rx={8.5} ry={11} fill={`url(#${g('p')})`} />
              <ellipse cx={7} cy={0} rx={8.5} ry={11} fill={`url(#${g('p')})`} />
              <ellipse cx={0} cy={0} rx={9.5} ry={12} fill={`url(#${g('p')})`} stroke="#c2410c" strokeWidth={0.8} />
              <g className="hx-pulse" style={{ animationDuration: '0.9s' }} fill="#fde047">
                <path d="M-8.5 -2 L-4.5 -7 L-1.8 -1.5 Z" />
                <path d="M8.5 -2 L4.5 -7 L1.8 -1.5 Z" />
                <path d="M-9 3 Q 0 11, 9 3 L 6 4.5 L 4 2.5 L 1.5 5 L -1.5 2.5 L -4 5 L -6 2.5 Z" />
              </g>
            </g>
          </g>
        </>
      );
    case 'halo':
      return (
        <>
          <defs>
            <filter id={g('halo')} x="-30%" y="-100%" width="160%" height="300%">
              <feGaussianBlur stdDeviation="1.6" />
            </filter>
          </defs>
          <g className="hx-bob">
            <ellipse cx={C} cy={7} rx={27} ry={6} fill="none" stroke="#fde68a" strokeWidth={5} filter={`url(#${g('halo')})`} opacity={0.8} />
            <ellipse cx={C} cy={7} rx={27} ry={6} fill="none" stroke="#fbbf24" strokeWidth={2.4} />
          </g>
        </>
      );
    case 'cat-ears':
      return (
        <>
          <path d="M18 43 L21 5 L50 16 Z" fill="#2b2633" stroke="#1a1720" strokeWidth={1} strokeLinejoin="round" />
          <path d="M24 34 L25.5 14 L42 20 Z" fill="#f9a8d4" />
          <path d="M107 43 L104 5 L75 16 Z" fill="#2b2633" stroke="#1a1720" strokeWidth={1} strokeLinejoin="round" />
          <path d="M101 34 L99.5 14 L83 20 Z" fill="#f9a8d4" />
        </>
      );
    case 'hearts':
      return (
        <>
          <circle cx={C} cy={C} r={54} fill="none" stroke="#fda4af" strokeWidth={1.2} strokeDasharray="1 4" strokeLinecap="round" />
          <g className="hx-orbit">
            {around(4, 55).map(({ x, y, i }) => (
              <g key={i} transform={`translate(${x} ${y}) scale(${i % 2 ? 1.1 : 1.5})`}>
                <path d={HEART} fill={i % 2 ? '#fb7185' : '#f43f5e'} stroke="#fff" strokeWidth={0.6} />
              </g>
            ))}
          </g>
        </>
      );
    case 'stars':
      return (
        <>
          <circle cx={C} cy={C} r={54} fill="none" stroke="#c4b5fd" strokeWidth={1} opacity={0.6} />
          <g className="hx-orbit" style={{ animationDuration: '24s' }}>
            {around(5, 56, 10).map(({ x, y, i }) => (
              <g key={i} transform={`translate(${x} ${y}) scale(${i % 2 ? 0.9 : 1.4})`}>
                <path className="hx-twinkle" style={{ animationDelay: `${i * -0.5}s` }} d={SPARKLE} fill={i % 2 ? '#fde68a' : '#ffffff'} />
              </g>
            ))}
          </g>
        </>
      );
    case 'glitch':
      return (
        <>
          <circle className="hx-glitch" cx={C} cy={C} r={54} fill="none" stroke="#22d3ee" strokeWidth={2.5} opacity={0.85} style={{ mixBlendMode: 'screen' }} />
          <circle className="hx-glitch" cx={C + 1.5} cy={C - 1} r={54} fill="none" stroke="#f43f5e" strokeWidth={2.5} opacity={0.85} style={{ animationDelay: '-1.4s', mixBlendMode: 'screen' }} />
          <rect className="hx-glitch" x={6} y={78} width={18} height={3} fill="#22d3ee" style={{ animationDelay: '-0.7s' }} />
          <rect className="hx-glitch" x={101} y={40} width={14} height={2.5} fill="#f43f5e" style={{ animationDelay: '-2s' }} />
        </>
      );
    case 'phoenix': {
      // Blaze: fiery wings either side, beating slowly, and a crest of flame on top.
      const feather = 'M0 0 C 7 -5, 20 -7, 30 -2 C 20 1, 9 3, 0 0 Z';
      const wing = (side: 'l' | 'r') => (
        // The right wing is the left one mirrored, outside the animated group (a CSS animation's
        // transform replaces an SVG one). SVG_ORIGIN keeps these transforms about (0, 0).
        <g transform={side === 'l' ? undefined : `translate(${2 * C} 0) scale(-1 1)`} style={SVG_ORIGIN}>
          <g className="hx-wing">
            {[14, 30, 46, 62, 78].map((angle, i) => (
              <path
                key={i}
                d={feather}
                transform={`translate(${C - 40} ${C + 4}) rotate(${180 + angle}) scale(${1.2 - i * 0.09})`}
                style={SVG_ORIGIN}
                fill={`url(#${g('w')})`}
                stroke="#7c2d12"
                strokeOpacity={0.35}
                strokeWidth={0.6}
              />
            ))}
          </g>
        </g>
      );
      return (
        <>
          <defs>
            <linearGradient id={g('w')} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#ffe08a" />
              <stop offset="0.45" stopColor="#ff9a3d" />
              <stop offset="1" stopColor="#e11d48" />
            </linearGradient>
            <linearGradient id={g('c')} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor="#ff4d1f" />
              <stop offset="1" stopColor="#ffe08a" />
            </linearGradient>
          </defs>
          {wing('l')}
          {wing('r')}
          <circle cx={C} cy={C} r={52.5} fill="none" stroke={`url(#${g('w')})`} strokeWidth={2.5} />
          {[-14, 0, 14].map((dx, i) => (
            <g key={i} transform={`translate(${C + dx} ${C - 51}) rotate(${dx * 1.2})`} style={SVG_ORIGIN}>
              <path
                className="hx-flame"
                style={{ animationDelay: `${i * -0.3}s` }}
                d={i === 1 ? 'M0 0 C -5 -6, -3.5 -14, 0 -20 C 3.5 -14, 5 -6, 0 0 Z' : 'M0 0 C -3.5 -4, -2.5 -9, 0 -13 C 2.5 -9, 3.5 -4, 0 0 Z'}
                fill={`url(#${g('c')})`}
              />
            </g>
          ))}
        </>
      );
    }
    case 'inferno':
      // Blaze: a ring of crimson fire, white-hot inside, with sparks circling it.
      return (
        <>
          <defs>
            <linearGradient id={g('i')} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0" stopColor="#7c3aed" />
              <stop offset="0.45" stopColor="#e11d48" />
              <stop offset="1" stopColor="#ffb347" />
            </linearGradient>
            <filter id={g('glow')} x="-30%" y="-30%" width="160%" height="160%">
              <feGaussianBlur stdDeviation="2.5" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <g filter={`url(#${g('glow')})`}>
            <circle cx={C} cy={C} r={52} fill="none" stroke="#fff7ed" strokeWidth={1.5} strokeOpacity={0.9} />
            <circle className="hx-pulse" cx={C} cy={C} r={54} fill="none" stroke={`url(#${g('i')})`} strokeWidth={4} />
            {around(24, 55).map(({ x, y, angle, i }) => (
              <g key={i} transform={`translate(${x} ${y}) rotate(${angle})`} style={SVG_ORIGIN}>
                <path
                  className="hx-flame"
                  style={{ animationDelay: `${(i % 6) * -0.15}s` }}
                  d={i % 3 === 0 ? 'M0 0 C -3 -5, -2 -11, 0 -16 C 2 -11, 3 -5, 0 0 Z' : 'M0 0 C -2.2 -3, -1.6 -6.5, 0 -9.5 C 1.6 -6.5, 2.2 -3, 0 0 Z'}
                  fill={`url(#${g('i')})`}
                />
              </g>
            ))}
          </g>
          <g className="hx-orbit">
            {around(5, 61, 20).map(({ x, y, i }) => (
              <circle key={i} cx={x} cy={y} r={i % 2 ? 1.4 : 2} fill="#ffd27a" />
            ))}
          </g>
        </>
      );
    default:
      return null;
  }
}

/** Overlay drawn on top of an avatar of `size` px. */
export function AvatarDecoration({ id, size }: { id: AvatarDecorationId; size: number }) {
  const uid = useId().replace(/:/g, '');
  const box = size * 1.25;
  if (id === 'rainbow') {
    return <span aria-hidden className="hx-anim hx-rainbow-ring pointer-events-none absolute rounded-full" style={{ width: box, height: box, left: (size - box) / 2, top: (size - box) / 2 }} />;
  }
  return (
    <svg
      aria-hidden
      viewBox="0 0 125 125"
      className="hx-anim hx-deco pointer-events-none absolute overflow-visible"
      style={{ width: box, height: box, left: (size - box) / 2, top: (size - box) / 2 }}
    >
      {art(id, uid)}
    </svg>
  );
}
